import { useTranslation } from '../i18n/TranslationContext';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, Button, Stack, Link } from '@mui/material';
import { getConsent, setConsent } from '../tracking/consent';
import { eventApi } from '../services/api';

interface BannerTexts {
  title: string | null;
  message: string | null;
  accept_label: string | null;
  reject_label: string | null;
  more_info_label: string | null;
  more_info_url: string | null;
  policy_content: string | null;
}

const DEFAULT_MESSAGE =
  'Questo sito utilizza cookie tecnici, necessari al suo funzionamento, e — solo con il tuo ' +
  'consenso — cookie di analisi e di profilazione, anche di terze parti, per misurare le visite ' +
  'e l’efficacia delle campagne pubblicitarie. Selezionando «Accetta» acconsenti ' +
  'all’uso di tutti i cookie; selezionando «Rifiuta» proseguirai la navigazione con ' +
  'i soli cookie tecnici. Puoi modificare o revocare la tua scelta in qualsiasi momento.';

const CookieConsentBanner: React.FC = () => {
  const { t, lang } = useTranslation();
  const defaultMessage = t('cookie.messaggio', DEFAULT_MESSAGE);
  const [visible, setVisible] = useState(getConsent() === null);
  const [texts, setTexts] = useState<BannerTexts | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }
    eventApi
      .getCookieBanner(lang)
      .then((data) => setTexts(data))
      .catch(() => setTexts(null));
  }, [visible, lang]);

  if (!visible) {
    return null;
  }

  const choose = (status: 'granted' | 'denied') => {
    setConsent(status);
    setVisible(false);
  };

  const moreInfoLabel = texts?.more_info_label || t('cookie.info', 'Clicca qui per maggiori informazioni');

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.snackbar,
        p: 2,
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: 960,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          {texts?.title && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {texts.title}
            </Typography>
          )}
          <Typography variant="body2">
            {texts?.message || defaultMessage}{' '}
            {texts?.more_info_url ? (
              <Link href={texts.more_info_url} target="_blank" rel="noopener noreferrer">
                {moreInfoLabel}
              </Link>
            ) : (
              <Link component={RouterLink} to="/cookie-policy">
                {moreInfoLabel}
              </Link>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" color="inherit" size="small" onClick={() => choose('denied')}>
            {texts?.reject_label || t('cookie.rifiuta', 'Rifiuta')}
          </Button>
          <Button variant="contained" size="small" onClick={() => choose('granted')}>
            {texts?.accept_label || t('cookie.accetta', 'Accetta')}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default CookieConsentBanner;
